import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import MenuItem from "../../../components/atoms/menu/menuItem";
import {
  eventCatalogueRoute,
  metricsRoute,
  specialDaysRoute
} from "../../../routes";
import {useStores} from "../../../stores/useStores";
import NavbarTitle from "../navbarTitle/navbarTitle";
import AccordionGroup from "../../../components/molecules/accordionGroup/accordionGroup";
import {IconSource} from "../../../shared/enums/iconSource";
import "./adminMenuSection.sass";
import {RoutePaths} from "../../../shared/routePaths";
import {useHistory} from "react-router";
import {QueryParams} from "../../../shared/enums/queryParams";
import {Navigation} from "../../../utils/navigation";
import {NavLink} from "react-router-dom";
import {NavItem} from "reactstrap";
import {EventCalendarType} from "../../../shared/enums/eventCalendarType";
import {eventCalendarTypeDisplay} from "../../../shared/eventCalendarTypeHelper";

/**
 * The admin section on vertical navigation menu.
 */
const AdminMenuSection: React.FC = () => {
  const [showAdminSection, setShowAdminSection] = useState(false);
  const {t} = useTranslation(["translation"]);
  const history = useHistory();
  const {
    uiStore: {accountStore},
    uiStore
  } = useStores();

  useEffect(() => {
    setShowAdminSection(
      eventCatalogueRoute.canBeAccessed!(accountStore.abilities) ||
        specialDaysRoute.canBeAccessed!(accountStore.abilities) ||
        metricsRoute.canBeAccessed!(accountStore.abilities)
    );
  }, [accountStore.abilities]);

  const isOnEventCataloguePage = history.location.pathname.startsWith(
    RoutePaths.EventCatalogue
  );
  const isOnSpecialDayPage = history.location.pathname.startsWith(
    RoutePaths.SpecialDays
  );
  const isOnMetricsPage = history.location.pathname.startsWith(
    RoutePaths.Metrics
  );
  const eventCatalogueName = t(eventCatalogueRoute.name);
  const enhancedCatalogueName = t(
    eventCalendarTypeDisplay[EventCalendarType.Enhanced].catalogueTitleKey
  );
  const specialDaysName = t("translation:titles.specialDays");
  const metricsName = t("translation:titles.metrics");
  const params = new URLSearchParams(history.location.search);
  const catalogueQueryParam = decodeURI(params.get(QueryParams.type) || "");

  useEffect(() => {
    if (!catalogueQueryParam && isOnEventCataloguePage) {
      Navigation.navigateWithParams(
        history,
        RoutePaths.EventCatalogue,
        QueryParams.type,
        encodeURI(enhancedCatalogueName)
      );
    }
  }, [
    catalogueQueryParam,
    enhancedCatalogueName,
    history,
    isOnEventCataloguePage
  ]);
  const catalogues = (
    <div className="catalogues">
      {Object.values(EventCalendarType).map((calendarType) => {
        const {catalogueTitleKey, icon} =
          eventCalendarTypeDisplay[calendarType];
        const name = t(catalogueTitleKey);
        const isSelected =
          isOnEventCataloguePage && catalogueQueryParam === name;
        return (
          <MenuItem
            key={name}
            title={name}
            icon={icon}
            selected={isSelected}
            scoped={isSelected}
            onClick={() => {
              Navigation.navigateWithParams(
                history,
                RoutePaths.EventCatalogue,
                QueryParams.type,
                encodeURI(name),
                [QueryParams.calendar]
              );
            }}
          />
        );
      })}
    </div>
  );
  const [isExpanded, setIsExpanded] = useState(isOnEventCataloguePage);
  return (
    <div className="admin-menu-section">
      {showAdminSection && (
        <NavbarTitle title={t("translation:labels.admin")} />
      )}
      <div className="admin-menu-options">
        {eventCatalogueRoute.canBeAccessed!(accountStore.abilities) && (
          <AccordionGroup
            accordions={[
              {
                id: eventCatalogueName,
                header: () => (
                  <MenuItem
                    title={eventCatalogueName}
                    icon={eventCatalogueRoute.icon}
                    selected={isOnEventCataloguePage}
                    scoped={isOnEventCataloguePage}
                  />
                ),
                children: catalogues,
                toggleIcon: IconSource.toggleUp,
                toggleIconColor: isOnEventCataloguePage
                  ? "primary"
                  : "secondary",
                isExpanded: isExpanded,
                onAccordionClick: (_, isExpanded) => {
                  setIsExpanded(isExpanded);
                }
              }
            ]}
          />
        )}
        {specialDaysRoute.canBeAccessed!(accountStore.abilities) && (
          <MenuItem
            key={specialDaysName}
            title={specialDaysName}
            icon={specialDaysRoute.icon}
            selected={isOnSpecialDayPage}
            scoped={isOnSpecialDayPage}
            onClick={() => {
              Navigation.navigateWithSelectedVillageId(
                history,
                RoutePaths.SpecialDays
              );
            }}
          />
        )}
        {metricsRoute.canBeAccessed!(accountStore.abilities) && (
          <MenuItem
            key={metricsName}
            title={metricsName}
            icon={metricsRoute.icon}
            selected={isOnMetricsPage}
            scoped={isOnMetricsPage}
            onClick={() => {
              Navigation.navigateWithSelectedVillageId(
                history,
                RoutePaths.Metrics
              );
            }}
          />
        )}
      </div>
    </div>
  );
};

export default AdminMenuSection;
